import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Receipt, Plus, ArrowsClockwise, Eye, PaperPlaneTilt, CheckCircle, Trash, FileText, X, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import { http, API } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";

const invoicesApi = {
  list: () => http.get("/invoices").then((r) => r.data),
  get: (id) => http.get(`/invoices/${id}`).then((r) => r.data),
  create: (body) => http.post("/invoices", body).then((r) => r.data),
  update: (id, body) => http.patch(`/invoices/${id}`, body).then((r) => r.data),
  remove: (id) => http.delete(`/invoices/${id}`).then((r) => r.data),
  send: (id) => http.post(`/invoices/${id}/send`).then((r) => r.data),
  pdfUrl: (id) => `${API}/invoices/${id}/pdf`,
};
const jobsApi = { list: () => http.get("/jobs").then((r) => r.data) };

const STATUS_COLORS = {
  draft: "#71717a",
  sent: "#4682b4",
  paid: "#22c55e",
  overdue: "#ef4444",
  void: "#a1a1aa",
};

export default function InvoicesPage() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setInvoices(await invoicesApi.list()); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totals = invoices.reduce((acc, i) => {
    const cents = Math.round((i.total_usd || 0) * 100);
    if (i.status === "paid") acc.paid += cents;
    else if (i.status === "sent" || i.status === "overdue") acc.outstanding += cents;
    return acc;
  }, { paid: 0, outstanding: 0 });

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#a1a1aa]">Get Paid Faster</div>
          <h1 className="font-heading font-black text-3xl tracking-tight">Invoices</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={loading} className="px-3 py-1.5 border border-[#2a2a2a] hover:border-[#ff8c00] text-[10px] uppercase tracking-[0.2em] font-bold flex items-center gap-2" data-testid="invoices-refresh">
            <ArrowsClockwise size={11} weight="bold" className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button onClick={() => setCreating(true)} className="px-3 py-1.5 bg-[#ff8c00] text-black text-[10px] uppercase tracking-[0.2em] font-bold flex items-center gap-2" data-testid="new-invoice">
            <Plus size={11} weight="bold" /> New Invoice
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Tile label="Total invoices" value={invoices.length} accent="#a1a1aa" testid="inv-tile-total" />
        <Tile label="Outstanding" value={`$${(totals.outstanding / 100).toLocaleString()}`} accent="#ff8c00" testid="inv-tile-out" />
        <Tile label="Paid" value={`$${(totals.paid / 100).toLocaleString()}`} accent="#22c55e" testid="inv-tile-paid" />
        <Tile label="Sent (not paid)" value={invoices.filter((i) => i.status === "sent").length} accent="#4682b4" testid="inv-tile-sent" />
      </div>

      <div className="bg-[#0d0d0d] border border-[#2a2a2a]">
        <div className="px-4 py-3 border-b border-[#2a2a2a] flex items-center justify-between">
          <h2 className="font-heading font-bold text-sm tracking-tight uppercase">Recent Invoices</h2>
        </div>
        {invoices.length === 0 ? (
          <div className="px-4 py-14 text-center text-xs text-[#71717a] uppercase tracking-[0.25em]" data-testid="inv-empty">
            No invoices yet — tap <strong>New Invoice</strong> to generate one from a completed job.
          </div>
        ) : (
          <div className="divide-y divide-[#2a2a2a]">
            {invoices.map((i) => (
              <InvoiceRow key={i.id} inv={i} onChanged={load} onEdit={() => setEditingId(i.id)} />
            ))}
          </div>
        )}
      </div>

      {creating && (
        <InvoiceFormDialog
          mode="create"
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); load(); }}
        />
      )}
      {editingId && (
        <InvoiceFormDialog
          mode="edit"
          invoiceId={editingId}
          onClose={() => setEditingId(null)}
          onSaved={() => { setEditingId(null); load(); }}
        />
      )}
    </div>
  );
}

function Tile({ label, value, accent, testid }) {
  return (
    <div className="bg-[#0d0d0d] border border-[#2a2a2a] p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.25em]" style={{ color: accent }}>{label}</div>
      <div className="text-2xl font-heading font-black mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function InvoiceRow({ inv, onChanged, onEdit }) {
  const send = async () => {
    if (!inv.customer_email) {
      toast.error("Add a customer email first");
      onEdit();
      return;
    }
    try {
      await invoicesApi.send(inv.id);
      toast.success("Invoice emailed");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
  };
  const markPaid = async () => {
    try {
      await invoicesApi.update(inv.id, { status: "paid" });
      toast.success("Marked paid");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't mark paid"); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${inv.invoice_number}?`)) return;
    try { await invoicesApi.remove(inv.id); toast.success("Deleted"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="px-4 py-3 flex items-center gap-3 flex-wrap" data-testid={`invoice-row-${inv.id}`}>
      <div className="w-9 h-9 bg-[#ff8c00]/15 border border-[#ff8c00]/50 flex items-center justify-center shrink-0">
        <Receipt size={14} className="text-[#ff8c00]" weight="bold" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono font-bold text-[#ff8c00] text-sm">{inv.invoice_number}</span>
          <span
            className="text-[9px] uppercase tracking-[0.25em] font-black px-1.5 py-0.5"
            style={{ background: STATUS_COLORS[inv.status] || "#71717a", color: "#000" }}
            data-testid={`invoice-status-${inv.id}`}
          >
            {inv.status}
          </span>
          <span className="text-sm text-white truncate">{inv.customer}</span>
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717a] font-mono mt-0.5">
          Issued {(inv.issued_at || "").slice(0, 10)} · Due {(inv.due_at || "").slice(0, 10) || "—"}
          {inv.customer_email && <span> · {inv.customer_email}</span>}
        </div>
      </div>
      <div className="text-right shrink-0 mr-2">
        <div className="font-mono font-black text-base text-white">${(inv.total_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
        {inv.tax_pct > 0 && <div className="text-[9px] text-[#71717a]">incl. {inv.tax_pct}% tax</div>}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <a href={invoicesApi.pdfUrl(inv.id)} target="_blank" rel="noreferrer" className="w-7 h-7 border border-[#2a2a2a] hover:border-[#4682b4] hover:text-[#4682b4] flex items-center justify-center" title="View PDF" data-testid={`invoice-pdf-${inv.id}`}>
          <FileText size={11} weight="bold" />
        </a>
        <button onClick={onEdit} className="w-7 h-7 border border-[#2a2a2a] hover:border-[#ff8c00] hover:text-[#ff8c00] flex items-center justify-center" title="Edit" data-testid={`invoice-edit-${inv.id}`}>
          <Eye size={11} weight="bold" />
        </button>
        {inv.status === "draft" && (
          <button onClick={send} className="w-7 h-7 border border-[#2a2a2a] hover:border-[#4682b4] hover:text-[#4682b4] flex items-center justify-center" title="Email to customer" data-testid={`invoice-send-${inv.id}`}>
            <PaperPlaneTilt size={11} weight="bold" />
          </button>
        )}
        {inv.status !== "paid" && inv.status !== "draft" && (
          <button onClick={markPaid} className="w-7 h-7 border border-[#2a2a2a] hover:border-[#22c55e] hover:text-[#22c55e] flex items-center justify-center" title="Mark paid" data-testid={`invoice-paid-${inv.id}`}>
            <CheckCircle size={11} weight="bold" />
          </button>
        )}
        <button onClick={remove} className="w-7 h-7 border border-[#2a2a2a] hover:border-[#ef4444] hover:text-[#ef4444] flex items-center justify-center" title="Delete" data-testid={`invoice-delete-${inv.id}`}>
          <Trash size={11} weight="bold" />
        </button>
      </div>
    </div>
  );
}

function InvoiceFormDialog({ mode, invoiceId, onClose, onSaved }) {
  const [jobs, setJobs] = useState([]);
  const [form, setForm] = useState({
    job_id: "", customer: "", customer_email: "", customer_address: "",
    line_items: [{ description: "", quantity: 1, unit_price_usd: 0 }],
    tax_pct: 0, terms: "Net 30", notes: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (mode === "create") {
      jobsApi.list().then(setJobs).catch(() => setJobs([]));
    } else if (invoiceId) {
      invoicesApi.get(invoiceId).then((inv) => setForm({
        ...inv,
        line_items: inv.line_items?.length ? inv.line_items : [{ description: "", quantity: 1, unit_price_usd: 0 }],
      }));
    }
  }, [mode, invoiceId]);

  const totals = (() => {
    const sub = (form.line_items || []).reduce((a, li) => a + (Number(li.quantity || 0) * Number(li.unit_price_usd || 0)), 0);
    const tax = sub * (Number(form.tax_pct || 0) / 100);
    return { sub, tax, total: sub + tax };
  })();

  const save = async () => {
    setLoading(true);
    try {
      if (mode === "create") {
        await invoicesApi.create({
          job_id: form.job_id || null,
          customer: form.customer || undefined,
          customer_email: form.customer_email || undefined,
          customer_address: form.customer_address || undefined,
          line_items: form.line_items.filter((li) => li.description?.trim()),
          tax_pct: Number(form.tax_pct || 0),
          terms: form.terms || "Net 30",
          notes: form.notes || undefined,
        });
        toast.success("Invoice created");
      } else {
        await invoicesApi.update(invoiceId, {
          customer: form.customer,
          customer_email: form.customer_email || null,
          customer_address: form.customer_address || null,
          line_items: form.line_items.filter((li) => li.description?.trim()),
          tax_pct: Number(form.tax_pct || 0),
          terms: form.terms,
          notes: form.notes,
        });
        toast.success("Invoice updated");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-[1000] flex items-center justify-center p-4" onClick={onClose} data-testid="invoice-dialog">
      <div className="bg-[#0d0d0d] border border-[#2a2a2a] w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[#2a2a2a] flex items-center justify-between">
          <h2 className="font-heading font-black text-lg tracking-tight">{mode === "create" ? "New Invoice" : `Edit ${form.invoice_number || ""}`}</h2>
          <button onClick={onClose} className="text-[#71717a] hover:text-white" data-testid="invoice-dialog-close"><X size={16} weight="bold" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {mode === "create" && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] block mb-1">Generate from Job (optional · auto-fills customer)</label>
              <select
                value={form.job_id}
                onChange={(e) => {
                  const jid = e.target.value;
                  const j = jobs.find((x) => x.id === jid);
                  setForm({
                    ...form, job_id: jid,
                    customer: j?.customer || form.customer,
                    customer_email: j?.customer_email || form.customer_email,
                    customer_address: j?.dropoff_address || form.customer_address,
                    line_items: j ? [{
                      description: `Transportation services — ${j.job_number} · ${j.pickup_address} → ${j.dropoff_address}`,
                      quantity: 1, unit_price_usd: form.line_items[0]?.unit_price_usd || 0,
                    }] : form.line_items,
                  });
                }}
                className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] outline-none text-sm"
                data-testid="invoice-job-select"
              >
                <option value="">— None (custom invoice) —</option>
                {jobs.filter((j) => j.status === "completed").map((j) => (
                  <option key={j.id} value={j.id}>{j.job_number} · {j.customer}</option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Customer" required>
              <input value={form.customer || ""} onChange={(e) => setForm({ ...form, customer: e.target.value })} className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] outline-none text-sm" data-testid="invoice-customer" />
            </Field>
            <Field label="Customer Email">
              <input type="email" value={form.customer_email || ""} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] outline-none text-sm" data-testid="invoice-email" />
            </Field>
          </div>
          <Field label="Billing Address">
            <textarea rows={2} value={form.customer_address || ""} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] outline-none text-sm" />
          </Field>

          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] mb-2">Line Items</div>
            <div className="space-y-2" data-testid="invoice-line-items">
              {form.line_items.map((li, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2">
                  <input placeholder="Description" value={li.description || ""}
                    onChange={(e) => {
                      const next = [...form.line_items]; next[idx] = { ...li, description: e.target.value };
                      setForm({ ...form, line_items: next });
                    }}
                    className="col-span-7 px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] outline-none text-sm"
                    data-testid={`invoice-li-desc-${idx}`} />
                  <input type="number" step="0.01" placeholder="Qty" value={li.quantity}
                    onChange={(e) => { const next = [...form.line_items]; next[idx] = { ...li, quantity: parseFloat(e.target.value) || 0 }; setForm({ ...form, line_items: next }); }}
                    className="col-span-2 px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] outline-none text-sm" />
                  <input type="number" step="0.01" placeholder="Unit $" value={li.unit_price_usd}
                    onChange={(e) => { const next = [...form.line_items]; next[idx] = { ...li, unit_price_usd: parseFloat(e.target.value) || 0 }; setForm({ ...form, line_items: next }); }}
                    className="col-span-2 px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] outline-none text-sm" />
                  <button type="button" onClick={() => setForm({ ...form, line_items: form.line_items.filter((_, i) => i !== idx) })}
                    className="col-span-1 border border-[#2a2a2a] hover:border-[#ef4444] hover:text-[#ef4444] flex items-center justify-center"
                    disabled={form.line_items.length === 1}><Trash size={11} weight="bold" /></button>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => setForm({ ...form, line_items: [...form.line_items, { description: "", quantity: 1, unit_price_usd: 0 }] })}
              className="mt-2 text-[10px] uppercase tracking-[0.2em] text-[#ff8c00] hover:underline flex items-center gap-1" data-testid="invoice-add-line">
              <Plus size={10} weight="bold" /> Add line
            </button>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Tax %">
              <input type="number" step="0.01" value={form.tax_pct} onChange={(e) => setForm({ ...form, tax_pct: e.target.value })} className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] outline-none text-sm" data-testid="invoice-tax" />
            </Field>
            <Field label="Terms">
              <input value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] outline-none text-sm" />
            </Field>
            <div className="bg-[#1a1a1a] border border-[#ff8c00] px-3 py-2 text-right">
              <div className="text-[9px] uppercase tracking-[0.25em] text-[#71717a]">Total</div>
              <div className="font-mono font-black text-lg text-[#ff8c00]" data-testid="invoice-total">${totals.total.toFixed(2)}</div>
            </div>
          </div>

          <Field label="Notes">
            <textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] outline-none text-sm" />
          </Field>
        </div>
        <div className="px-5 py-3 border-t border-[#2a2a2a] flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-[#2a2a2a] text-[#a1a1aa] text-[10px] uppercase tracking-[0.25em] font-bold">Cancel</button>
          <button onClick={save} disabled={loading || !form.customer} className="px-4 py-2 bg-[#ff8c00] disabled:opacity-40 text-black text-[10px] uppercase tracking-[0.25em] font-black flex items-center gap-2" data-testid="invoice-save">
            <FloppyDisk size={11} weight="bold" /> {loading ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] block mb-1">
        {label} {required && <span className="text-[#ff8c00]">*</span>}
      </label>
      {children}
    </div>
  );
}
